import { SkeletonReveal } from "@albertine/design-system/components/SkeletonReveal";
import type { Tenant } from "albertine-shared-web";
import type { ReactNode } from "react";
import type { SkeletonRevealPageState } from "@/routes/-hooks/useSkeletonRevealPage";
import { DocumentTitle } from "../DocumentTitle";
import { ShareButton } from "../ShareButton";
import { StickyHeader, StickyHeaderBackButton } from "../StickyHeader";

export const BOOKING_HERO_BACKGROUND = "var(--components-booking-hero-background)";
export const BOOKING_FOOTER_BACKGROUND = "var(--color-background-booking-secondary)";

/**
 * Add next to a section's `bg-*` class. Keeps the section transparent, showing the
 * single body color, until the page's skeleton reveal has settled.
 * Relies on `BookingPageLayout` marking its root `group/booking` with `data-revealed`.
 */
export const BOOKING_REVEAL_BACKGROUND_CLASS =
    "transition-[background-color] duration-700 ease-out group-data-[revealed=false]/booking:bg-transparent";

type ShareAction = {
    onShare: () => void;
    isPending?: boolean;
};

type BookingPageLayoutProps = {
    id: string;
    tenant: Tenant;
    title: string;
    documentTitle?: string;
    hero: ReactNode;
    children: ReactNode;
    backButton?: ReactNode;
    shareAction?: ShareAction;
    footer?: ReactNode;
    reveal?: SkeletonRevealPageState;
};

export function BookingPageLayout({
    id,
    tenant,
    title,
    documentTitle,
    hero,
    children,
    backButton,
    shareAction,
    footer,
    reveal,
}: BookingPageLayoutProps) {
    const isPlaceholder = reveal !== undefined && !reveal.skeletonRevealProps.isLoaded;
    const content = (
        <>
            {hero}
            {children}
            {footer}
        </>
    );

    return (
        <div
            id={id}
            className={`${tenant} group/booking relative w-full`}
            data-revealed={reveal?.isRevealed}
        >
            <DocumentTitle title={isPlaceholder ? "" : (documentTitle ?? title)} />
            <StickyHeader
                title={isPlaceholder ? "" : title}
                variant="booking"
                backButton={backButton ? <StickyHeaderBackButton /> : undefined}
                shareButton={
                    shareAction ? (
                        <ShareButton
                            placement="sticky"
                            onShare={shareAction.onShare}
                            isPending={shareAction.isPending}
                            isDisabled={isPlaceholder}
                        />
                    ) : undefined
                }
            />
            {/* Outside the reveal, which makes its content inert while loading. */}
            {backButton}
            {shareAction ? (
                <ShareButton
                    placement="overlay"
                    onShare={shareAction.onShare}
                    isPending={shareAction.isPending}
                    isDisabled={isPlaceholder}
                />
            ) : null}

            {reveal ? (
                <SkeletonReveal {...reveal.skeletonRevealProps}>
                    {content}
                </SkeletonReveal>
            ) : (
                content
            )}
        </div>
    );
}
